import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { StockBadge } from './StockBadge';
import { getAvailableStock } from '@/supabase/inventory/types/inventory';
import type {
  AdjustMode,
  InventoryOverviewItem,
  MovementType,
  RecordMovementInput,
} from '@/supabase/inventory/types/inventory';

export type MovementModalMode = 'movement' | 'adjust';

export interface MovementModalHandle {
  // PR3 리뷰 수용: open의 item을 InventoryOverviewItem으로 넓힌다 — 목록에서 실제로 넘어오는
  // 런타임 값이 reserved를 포함한 overview item이라(useInventory 소스), 배지가 가용을 계산하려면
  // 타입에 reserved가 있어야 한다. 호출부(Inventory.tsx)가 항상 이 값을 넘기므로 정직한 확장이다.
  open: (item: InventoryOverviewItem, mode: MovementModalMode) => void;
}

interface MovementModalContainerProps {
  recordMovement: (input: RecordMovementInput) => Promise<boolean>;
  isSubmitting: boolean;
}

const TOGGLE_ITEM_CLASS =
  'text-xs px-3 h-8 rounded-md bg-transparent text-gray-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-indigo-700';

// 정수 강제 + NaN 가드. 수량은 서버 int 컬럼이라 소수는 애초에 유효하지 않은 입력으로 취급한다.
// 빈 문자열은 Number('') === 0으로 평가돼 조정(set)모드에서 미입력이 '0'으로 통과하는
// 함정이 있어 trim 후 명시적으로 null 처리한다(빈 값 ≠ 타이핑한 0).
function parseIntQty(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isInteger(n)) return null;
  return n;
}

/**
 * 입출고/조정 모달을 Inventory 페이지에서 분리해 자체 관리한다(입출고·조정 두 모드를 하나의
 * 컨테이너가 겸한다 — 헤더·수량·메모 구조가 동일하고 라디오 선택지만 달라 별도 파일로
 * 쪼갤 만큼의 분기가 아니다). 대상 상품/모드는 `open(item, mode)` 인자로만 시딩되고
 * imperative handle은 `open`만 노출 → 부모는 ref 호출만 하므로 모달 오픈 시 부모가
 * 재렌더되지 않는다.
 *
 * ⚠️ React.memo로 감싸지 말 것. isSubmitting은 제출 중 버튼 비활성화(중복 제출 가드)를
 *    실시간으로 반영해야 하는 부모 live prop이다. 지금은 매 렌더 원시값이라 memo를 씌워도
 *    정상 반영되지만, 다른 모달 컨테이너 선례(CombinedShippingModalContainer)와 동일하게
 *    "open-state를 내부에 격리한 컨테이너는 항상 memo 금지"로 통일해 향후 prop 안정화
 *    리팩터가 조용히 재렌더를 생략시키는 사고를 막는다.
 */
export const MovementModalContainer = forwardRef<MovementModalHandle, MovementModalContainerProps>(
  ({ recordMovement, isSubmitting }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState<MovementModalMode>('movement');
    const [item, setItem] = useState<InventoryOverviewItem | null>(null);
    const [movementType, setMovementType] = useState<MovementType>('입고');
    const [adjustMode, setAdjustMode] = useState<AdjustMode>('set');
    const [qty, setQty] = useState('');
    const [memo, setMemo] = useState('');
    // 진행 중이던 제출(await)이 끝나기 전에 모달이 닫혔다가 다른 대상으로 재오픈되면,
    // 그 응답이 새 세션의 폼/오픈 상태를 건드리지 않도록 세대를 구분한다.
    const sessionRef = useRef(0);

    const resetForm = useCallback(() => {
      setMovementType('입고');
      setAdjustMode('set');
      setQty('');
      setMemo('');
    }, []);

    const open = useCallback<MovementModalHandle['open']>(
      (nextItem, nextMode) => {
        sessionRef.current += 1;
        setItem(nextItem);
        setMode(nextMode);
        resetForm();
        setIsOpen(true);
      },
      [resetForm],
    );

    useImperativeHandle(ref, () => ({ open }), [open]);

    const handleOpenChange = useCallback(
      (next: boolean) => {
        if (!next) {
          sessionRef.current += 1;
          resetForm();
        }
        setIsOpen(next);
      },
      [resetForm],
    );

    const parsedQty = parseIntQty(qty);
    const isQtyValid =
      parsedQty !== null && (mode === 'adjust' && adjustMode === 'set' ? parsedQty >= 0 : parsedQty > 0);

    const handleSubmit = useCallback(async () => {
      if (item === null || parsedQty === null) return;
      if (mode === 'adjust' && adjustMode === 'set' ? parsedQty < 0 : parsedQty <= 0) return;

      const session = sessionRef.current;
      const trimmedMemo = memo.trim();
      const input: RecordMovementInput =
        mode === 'movement'
          ? { itemId: item.id, type: movementType, qty: parsedQty, memo: trimmedMemo || undefined }
          : { itemId: item.id, type: '조정', qty: parsedQty, mode: adjustMode, memo: trimmedMemo || undefined };

      const success = await recordMovement(input);
      if (session !== sessionRef.current) return;
      if (success) {
        resetForm();
        setIsOpen(false);
      }
    }, [item, parsedQty, mode, adjustMode, movementType, memo, recordMovement, resetForm]);

    const title = mode === 'movement' ? '입출고 기록' : '재고 조정';
    // 조정 설명·placeholder는 방식별 분리(오너 2026-07-13) — '실사'는 set(상태 선언)의
    // 시나리오고 추가/차감은 원인 있는 사건(변화량 선언)이라, 공용 문구로 합치면 의미가 어긋난다.
    const description =
      mode === 'movement'
        ? '입고 또는 출고 수량을 기록하면 재고에 바로 반영됩니다.'
        : adjustMode === 'set'
          ? '실사로 확인한 실제 수량을 입력하면 장부가 그 값으로 맞춰집니다.'
          : '입출고로 기록하기 애매한 증감을 수량만큼 보정합니다.';

    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="break-keep">{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
              <span className="max-w-[70%] truncate text-sm font-medium text-slate-900" title={item?.name ?? ''}>
                {item?.name ?? ''}
              </span>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                {/* PR3 리뷰 수용(Task 3 finding): 목록은 가용(=stock−reserved) 기준으로 배지
                    색을 판정하는데 이 모달은 raw stock을 넘겨 같은 상품이 목록에선 빨강/주황,
                    모달에선 회색으로 보이는 불일치가 있었다. open()이 이제 InventoryOverviewItem을
                    받으므로 getAvailableStock으로 목록과 동일한 기준을 넘긴다. 배지는 숫자 자체를
                    렌더하므로 라벨도 '가용'으로 맞추고, set 모드('실제 수량으로 변경')가 조작하는
                    현재고 숫자를 옆에 병기한다 — 재고 산술(RPC 입력, set모드 delta 미리보기)은
                    전부 raw stock 그대로라 이 변경과 무관하다. */}
                <span className="tabular-nums">현재고 {(item?.stock ?? 0).toLocaleString('ko-KR')}</span>
                <span aria-hidden="true">·</span>
                가용
                <StockBadge
                  available={item ? getAvailableStock(item) : 0}
                  threshold={item?.low_stock_threshold ?? 0}
                />
              </div>
            </div>

            {mode === 'movement' ? (
              <div className="space-y-1.5">
                <Label>구분</Label>
                <ToggleGroup
                  type="single"
                  value={movementType}
                  onValueChange={(value) => {
                    if (value === '입고' || value === '출고') setMovementType(value);
                  }}
                  className="justify-start gap-1 rounded-lg bg-gray-200 p-1"
                >
                  <ToggleGroupItem value="입고" aria-label="입고" className={TOGGLE_ITEM_CLASS}>
                    입고
                  </ToggleGroupItem>
                  <ToggleGroupItem value="출고" aria-label="출고" className={TOGGLE_ITEM_CLASS}>
                    출고
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>조정 방식</Label>
                <ToggleGroup
                  type="single"
                  value={adjustMode}
                  onValueChange={(value) => {
                    if (value === 'set' || value === 'add' || value === 'subtract') setAdjustMode(value);
                  }}
                  className="justify-start gap-1 rounded-lg bg-gray-200 p-1"
                >
                  <ToggleGroupItem value="set" aria-label="실제 수량으로 변경" className={TOGGLE_ITEM_CLASS}>
                    실제 수량으로 변경
                  </ToggleGroupItem>
                  <ToggleGroupItem value="add" aria-label="추가" className={TOGGLE_ITEM_CLASS}>
                    추가
                  </ToggleGroupItem>
                  <ToggleGroupItem value="subtract" aria-label="차감" className={TOGGLE_ITEM_CLASS}>
                    차감
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="inventory-movement-qty">수량</Label>
              <Input
                id="inventory-movement-qty"
                type="number"
                min={0}
                autoFocus
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder={
                  mode === 'movement'
                    ? '수량'
                    : adjustMode === 'set'
                      ? '실사로 센 실제 수량'
                      : adjustMode === 'add'
                        ? '추가할 수량'
                        : '차감할 수량'
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inventory-movement-memo">메모 (선택)</Label>
              <Textarea
                id="inventory-movement-memo"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder={
                  mode === 'movement'
                    ? undefined
                    : adjustMode === 'set'
                      ? '예: 7월 실사 반영'
                      : adjustMode === 'add'
                        ? '예: 수량 착오 정정, 덤 수령'
                        : '예: 불량 폐기, 샘플 반출'
                }
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              취소
            </Button>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800"
              onClick={handleSubmit}
              disabled={isSubmitting || !isQtyValid}
            >
              기록
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

MovementModalContainer.displayName = 'MovementModalContainer';
